import { useState, useCallback, useEffect } from "react";
import {useNavigate} from "react-router-dom";
import { useCreateCase, useClients } from "../../../hooks/useWorkshopQueries";
import {PersonIcon, CarIcon, AlertIcon, CheckIcon,
        EmailInputIcon,PhoneInputIcon,LocationInputIcon,CarInputIcon,
        XIcon, EmailIcon, CopyIcon
      } from "../UI/icons";
import axios from "axios";

// ─── Constants ────────────────────────────────────────────────────────────────
const PRIORITIES = [
  { value: "Minor",   label: "Minor",   days: 10, color: "#22c55e", bg: "rgba(34,197,94,.1)",   border: "rgba(34,197,94,.3)",   icon: "🟢" },
  { value: "Average", label: "Average", days: 20, color: "#f59e0b", bg: "rgba(245,158,11,.1)",  border: "rgba(245,158,11,.3)",  icon: "🟡" },
  { value: "Major",   label: "Major",   days: 30, color: "#f97316", bg: "rgba(249,115,22,.1)",  border: "rgba(249,115,22,.3)",  icon: "🟠" },
  { value: "Serious", label: "Serious", days: 45, color: "#ef4444", bg: "rgba(239,68,68,.1)",   border: "rgba(239,68,68,.3)",   icon: "🔴" },
];

const STEPS = [
  { id: 1, label: "Customer",  icon: PersonIcon  },
  { id: 2, label: "Vehicle",   icon: CarIcon     },
  { id: 3, label: "Accident",  icon: AlertIcon   },
  { id: 4, label: "Review",    icon: CheckIcon   },
];

const EMPTY_FORM = {
  // Step 1 - Customer
  customerEmail:   "",
  customerPhone:   "",
  customerName:    "",
  clientId:        "",
  clientName:      "",
  // Step 2 - Vehicle
  vehicleNo:       "",
  policyNo:        "",
  insuranceCompany:"",
  insuranceExpiry: "",
  vehicleMake:     "",
  vehicleModel:    "",
  vehicleYear:     "",
  vehicleBodyType: "",
  // Step 3 - Accident
  accidentDate:    "",
  accidentTime:    "",
  accidentLocation:"",
  accidentPincode: "",
  accidentState:   "",
  description:     "",
  // Step 3 - Priority
  priority:        "Minor",
};

// Preset CC recipients the admin can quickly toggle
const CC_PRESETS = [
  { id: "ops",        label: "Ops Team",        email: "ops@claimpro.in"        },
  { id: "kam", label: "KAM Team",       email: "kam@claimpro.in" },
  { id: "cranes",   label: "Cranes Team",    email: "cranes@claimpro.in"  },
];


function formatForSQL(date, time) {
  if (!date || !time) return null;

  return `${date} ${time}:00`; // ✅ MySQL format
}

// ─── Validation per step ──────────────────────────────────────────────────────
function validateStep(step, form) {
  const errs = {};
  if (step === 1) {
    if (!form.customerEmail.trim()){
        errs.customerEmail = "Email is required";
    }
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.customerEmail)){
        errs.customerEmail = "Enter a valid email address";
    }
    if (!form.customerPhone.trim()){
        errs.customerPhone = "Phone is required";
    }
    else if (form.customerPhone && !/^\d{10}$/.test(form.customerPhone)){
        errs.customerPhone = "Must be 10 digits";
    }     
  }
  if (step === 2) {
    if (!form.vehicleNo.trim()){
        errs.vehicleNo = "Vehicle number is required";
    }
  //   if (!form.policyNo.trim()){
  //       errs.policyNo = "Policy number is required";
  // }
    }                                           
  if (step === 3) {
    // if (!form.accidentDate){
    //     errs.accidentDate = "Accident date is required";
    // }
    // if (!form.accidentTime) {
    //     errs.accidentTime = "Accident time is required";
    // }
    // if (!form.accidentLocation.trim()){
    //     errs.accidentLocation = "Location is required";
    // }
    // if (!form.accidentPincode.trim()){
    //     errs.accidentPincode = "Pincode is required";
    // }                         
  //  if (!/^\d{6}$/.test(form.accidentPincode)){
  //       errs.accidentPincode = "Must be 6 digits";
  //   }                   
  }
  return errs;
}

// ─── Main Modal Component ─────────────────────────────────────────────────────
export default function CreateCaseModal({ open, onClose }) {
  const [step,setStep] = useState(1);
  const [form,setForm] = useState(EMPTY_FORM);
  const [errors,setErrors] = useState({});
  const [submitting,setSubmitting] = useState(false);
  const [success,setSuccess] = useState(null); // { caseId, email }
  const [touched, setTouched] = useState({});
  const [apiError, setApiError] = useState(null);
  const [vehicleLookupLoading, setVehicleLookupLoading] = useState(false);
  const [ccEmails, setCcEmails] = useState([]); // string[]
  const [vehicleError, setVehicleError] = useState(null);

  const navigate = useNavigate();
  const { data: clientsData, isLoading: clientsLoading } = useClients();
  const clients = clientsData || [];

  useEffect(() => {
      if (!form.clientId) {
        setCcEmails([]);
        return;
      }
      const selected = clients.find(c => String(c.id) === String(form.clientId));
      if (selected?.ccEmails?.length) {
        setCcEmails(selected.ccEmails);
      } else {
        setCcEmails([]);
      }
  }, [form.clientId, clients]);

  // ── Real mutation — uncomment when backend ready ───────────────────────────
   const createCase = useCreateCase();

  const setField = useCallback((key, value) => {
    setForm(f => ({ ...f, [key]: value }));
    setTouched(t => ({ ...t, [key]: true }));
    // Clear error live
    setErrors(e => ({ ...e, [key]: undefined }));
  }, []);

  const handleNext = () => {
    const errs = validateStep(step, form);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      // Mark all fields in this step as touched
      setTouched(t => ({ ...t, ...Object.fromEntries(Object.keys(errs).map(k => [k, true])) }));
      return;
    }
    setErrors({});
    setStep(s => s + 1);
  };

  const handleBack = () => {
    setErrors({});
    setStep(s => s - 1);
  };



  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // ── Real API call ──────────────────────────────────────────────────────
      const result = await createCase.mutateAsync({
        customerEmail: form.customerEmail.trim(),
        customerNumber:    form.customerPhone || null,
        customerName:     form.customerName  || null,
        clientId: form.clientId || null,
        clientName: clients.find(c => String(c.id) === String(form.clientId))?.clientName  || form.clientName ||null,  
        vehicleNumber:    form.vehicleNo.toUpperCase().replace(/\s/g, ""),
        policyNumber: form.policyNo || null,
        insuranceCompany: form.insuranceCompany || null,
        insuranceExpiry: form.insuranceExpiry || null,
        vehicleMake: form.vehicleMake || null,
        vehicleModel: form.vehicleModel || null,
        vehicleYear: form.vehicleYear || null,
        vehicleBodyType: form.vehicleBodyType || null,
        accidentDateTime: formatForSQL(form.accidentDate, form.accidentTime),
        accidentLocation: form.accidentLocation?.trim() || null,
        accidentPincode: form.accidentPincode || null,
        accidentState: form.accidentState || null,
        accidentDescription: form.description || null,
        priority: form.priority,
        ccEmails,
      });
      
      setSuccess({ caseId: result.accidentCaseNo, email: form.customerEmail, ccEmails  });
   
    } catch (err) {
      console.error("Create case failed:", err);
       setApiError(
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      err.message ||
      "Something went wrong"
    );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset everything
    setStep(1);
    setForm(EMPTY_FORM);
    setErrors({});
    setTouched({});
    setSubmitting(false);
    setSuccess(null);
    setVehicleError(null);
    onClose();
  };

  const handleCloseAndNavigate = () => {
    handleClose(); // resets form state
    navigate("/admin/cases", { state: { highlightedCase: success?.caseId } });
  };

  if (!open) return null;

  const fetchVehicleData = async (vehicleNumber) => {
  if (!vehicleNumber || vehicleNumber.length < 6) return;

  setVehicleLookupLoading(true);
  setVehicleError(null);
  try {
    const response = await axios.get(`/api/vehicle-details/${vehicleNumber}`, {
      baseURL: process.env.REACT_APP_BACKEND_URL,
    });

    // Extract data from ULIP response
    const vd = response.data;
    if (!vd) {
       setVehicleError("not_found");
      return
    };

    if (vd.insuranceCompany)  setField("insuranceCompany", vd.insuranceCompany);
    if (vd.insurancePolicyNo) setField("policyNo",         vd.insurancePolicyNo);
    if (vd.insuranceExpiry)   setField("insuranceExpiry",  vd.insuranceExpiry);
    if (vd.vehicleMake)       setField("vehicleMake",      vd.vehicleMake);
    if (vd.vehicleModel)      setField("vehicleModel",     vd.vehicleModel);
    if (vd.vehicleYear)       setField("vehicleYear",      vd.vehicleYear);
    if (vd.vehicleBodyType)   setField("vehicleBodyType",  vd.vehicleBodyType);

  } catch (error) {
    console.error("Error fetching vehicle details:", error);
    const status = error?.response?.status;
    setVehicleError(status === 404 ? "not_found" : "lookup_failed");
  } finally {
    setVehicleLookupLoading(false);
  }
};

const handleVehicleNumberChange = (value) => {
  setField("vehicleNo", value.toUpperCase());

  if (vehicleError) setVehicleError(null);
  if (value.replace(/[^A-Z0-9]/g, "").length >= 6) {
    fetchVehicleData(value);
  }
};

  return (
    <div className="wd-overlay" onClick={handleClose} style={{ alignItems:"center" }}>
      <div
        className="wd-animate"
        onClick={e => e.stopPropagation()}
        style={{
          background:"var(--wd-card)",
          border:"1px solid var(--wd-border-md)",
          borderRadius:"var(--wd-r-xl)",
          width:"100%",
          maxWidth: success ? 480 : 560,
          maxHeight:"92vh",
          overflowY:"auto",
          boxShadow:"0 32px 96px rgba(0,0,0,.85)",
        }}
      >
        {success
          ? <SuccessScreen success={success} onClose={handleCloseAndNavigate} onCreateAnother={() => { setStep(1); setForm(EMPTY_FORM); setErrors({}); setTouched({}); setSuccess(null); }} />
          : <FormScreen
                step={step} form={form} errors={errors} touched={touched}
              setField={setField} submitting={submitting}
              ccEmails={ccEmails} setCcEmails={setCcEmails}
              onNext={handleNext} onBack={handleBack} onSubmit={handleSubmit} onClose={handleClose}
              onVehicleChange={handleVehicleNumberChange} vehicleLookupLoading={vehicleLookupLoading}
              apiError={apiError}
              vehicleError={vehicleError}  
              onVehicleRetry={() => fetchVehicleData(form.vehicleNo)}
              clients={clients}
              clientsLoading={clientsLoading}
            />
        }
      </div>
    </div>
  );
}

// ─── Form Screen ──────────────────────────────────────────────────────────────
function FormScreen({ step, form, errors, touched, setField, submitting, ccEmails, setCcEmails, onNext, onBack, onSubmit, onClose, onVehicleChange, vehicleLookupLoading, apiError , vehicleError, onVehicleRetry, clients, clientsLoading }) {
  return (
    <>
      {/* Header */}
      <div style={{ padding:"20px 24px 0", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div>
          <div style={{ fontSize:17, fontWeight:700, color:"var(--wd-t1)" }}>Create New Case</div>
          <div style={{ fontSize:12, color:"var(--wd-t3)", marginTop:2 }}>Step {step} of {STEPS.length}</div>
        </div>
        <button className="wd-btn wd-btn-ghost wd-btn-icon wd-btn-sm" onClick={onClose}>
          <XIcon />
        </button>
      </div>

      {/* Step indicator */}
      <div style={{ padding:"16px 24px 0" }}>
        <StepIndicator step={step} />
      </div>

      {/* Divider */}
      <div style={{ height:1, background:"var(--wd-border)", margin:"16px 0 0" }} />

      {/* Step Content */}
      <div style={{ padding:"24px", display:"flex", flexDirection:"column", gap:16 }}>
        {step === 1 && <Step1 form={form} errors={errors} touched={touched} setField={setField} clients={clients} clientsLoading={clientsLoading} />}
        {step === 2 && <Step2 form={form} errors={errors} touched={touched} setField={setField} onVehicleChange={onVehicleChange}
      vehicleLookupLoading={vehicleLookupLoading} vehicleError={vehicleError} onVehicleRetry={onVehicleRetry}/>}
        {step === 3 && <Step3 form={form} errors={errors} touched={touched} setField={setField} />}
         {step === 4 && <Step4 form={form} ccEmails={ccEmails} setCcEmails={setCcEmails} clientCcEmails={clients.find(c => String(c.id) === String(form.clientId))?.ccEmails || []} />}
      </div>

      {/* Footer */}
      <div style={{ padding:"16px 24px", borderTop:"1px solid var(--wd-border)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          {step > 1 && (
            <button className="wd-btn wd-btn-ghost" onClick={onBack} disabled={submitting}>
              ← Back
            </button>
          )}
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button className="wd-btn wd-btn-ghost" onClick={onClose} disabled={submitting}>Cancel</button>
          {step < STEPS.length
            ? <button className="wd-btn wd-btn-primary" onClick={onNext}>Continue →</button>
            : <button className="wd-btn wd-btn-primary" onClick={onSubmit} disabled={submitting}
                style={{ minWidth:140, justifyContent:"center" }}>
                {submitting
                  ? <><SpinnerInline /> Creating & Sending Email…</>
                  : <><EmailIcon /> Create & Send Email</>
                }
              </button>
          }
        </div>
      </div>
    </>
  );
}

// ─── Step Indicator ───────────────────────────────────────────────────────────
function StepIndicator({ step }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:0 }}>
      {STEPS.map((s, i) => {
        const done    = step > s.id;
        const active  = step === s.id;
        const last    = i === STEPS.length - 1;
        return (
          <div key={s.id} style={{ display:"flex", alignItems:"center", flex: last ? 0 : 1 }}>
            {/* Circle */}
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
              <div style={{
                width:32, height:32, borderRadius:"50%", flexShrink:0,
                display:"flex", alignItems:"center", justifyContent:"center",
                background: done ? "var(--wd-green)" : active ? "var(--wd-blue)" : "var(--wd-hover)",
                border:`2px solid ${done ? "var(--wd-green)" : active ? "var(--wd-blue)" : "var(--wd-border-md)"}`,
                transition:"all .2s",
              }}>
                {done
                  ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  : <s.icon active={active} />
                }
              </div>
              <span style={{ fontSize:10, fontWeight:600, color: active ? "var(--wd-blue)" : done ? "var(--wd-green)" : "var(--wd-t3)", textTransform:"uppercase", letterSpacing:".04em", whiteSpace:"nowrap" }}>
                {s.label}
              </span>
            </div>
            {/* Connector line */}
            {!last && (
              <div style={{ flex:1, height:2, background: done ? "var(--wd-green)" : "var(--wd-border-md)", margin:"0 8px", marginBottom:20, transition:"background .2s" }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Step 1: Customer Info ────────────────────────────────────────────────────
function Step1({ form, errors, touched, setField, clients, clientsLoading }) {
  return (
    <>
      <SectionLabel icon="👤" title="Customer Information" subtitle="Email is required. We'll send the case ID here." />

      <Field label="Customer Email *" error={touched.customerEmail && errors.customerEmail}>
        <div style={{ position:"relative" }}>
          <input
            className={`wd-input ${touched.customerEmail && errors.customerEmail ? "input-error" : ""}`}
            style={{ paddingLeft:36 }}
            type="email"
            placeholder="customer@example.com"
            value={form.customerEmail}
            onChange={e => setField("customerEmail", e.target.value)}
            autoFocus
          />
          <EmailInputIcon />
        </div>
        {touched.customerEmail && errors.customerEmail && <ErrorMsg>{errors.customerEmail}</ErrorMsg>}
        <HelperText>A case registration email with case ID will be sent to this address immediately after submission.</HelperText>
      </Field>

      <Field label="Customer Phone *" error={touched.customerPhone && errors.customerPhone}>
        <div style={{ position:"relative" }}>
          <input
            className={`wd-input ${touched.customerPhone && errors.customerPhone ? "input-error" : ""}`}
            style={{ paddingLeft:36 }}
            type="tel"
            placeholder="10-digit mobile number"
            value={form.customerPhone}
            onChange={e => setField("customerPhone", e.target.value.replace(/\D/g,"").slice(0,10))}
          />
          <PhoneInputIcon />
        </div>
        {touched.customerPhone && errors.customerPhone && <ErrorMsg>{errors.customerPhone}</ErrorMsg>}
      </Field>

      <Field label="Customer Name">
        <input
          className="wd-input"
          placeholder="Full name (optional — customer can update)"
          value={form.customerName}
          onChange={e => setField("customerName", e.target.value)}
        />
      </Field>

      {/* ── Client Name field ── */}
      <Field label="Client Name">
        <select
          className="wd-input"
          value={form.clientId || ""}
          onChange={(e) => {
            const selectedId = e.target.value;

            const selectedClient = clients.find(
              (client) => String(client.id) === String(selectedId)
            );

            setField("clientId", selectedId);
            setField("clientName", selectedClient?.clientName  || "");
          }}
          disabled={clientsLoading}
        >
          <option value="">
            {clientsLoading ? "Loading clients..." : "Select Client"}
          </option>

          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.clientName}
            </option>
          ))}
        </select>
      </Field>

      {/* Info card */}
      <div style={{ display:"flex", gap:10, padding:"12px 14px", background:"rgba(59,130,246,.07)", borderRadius:10, border:"1px solid rgba(59,130,246,.15)" }}>
        <span style={{ fontSize:16, flexShrink:0 }}>ℹ️</span>
        <div style={{ fontSize:12, color:"var(--wd-blue)", lineHeight:1.6 }}>
          Only <strong>email is mandatory</strong>. The customer will receive a link to complete their profile, 
          upload documents, and fill in driver details at their own convenience.
        </div>
      </div>
    </>
  );
}

// ─── Step 2: Vehicle & Policy ─────────────────────────────────────────────────
function Step2({ form, errors, touched, setField, onVehicleChange, vehicleLookupLoading, vehicleError, onVehicleRetry }) {
  return (
    <>
      <SectionLabel icon="🚗" title="Vehicle & Insurance" subtitle="Vehicle registration number and policy details." />
 
      <Field label="Vehicle Registration Number *" error={touched.vehicleNo && errors.vehicleNo}>
        <div style={{ position:"relative" }}>
          <input
            className={`wd-input wd-mono ${touched.vehicleNo && errors.vehicleNo ? "input-error" : ""}`}
            style={{ paddingLeft:36, letterSpacing:".06em", fontSize:15 }}
            placeholder="e.g. DL01AB1234"
            value={form.vehicleNo}
            onChange={e => onVehicleChange(e.target.value)}
            autoFocus
          />
          <CarInputIcon />
          {vehicleLookupLoading && (
            <div style={{
              position: "absolute", right: 10, top: "50%",
              transform: "translateY(-50%)",
            }}>
              <SpinnerInline />
            </div>
          )}
        </div>
        {touched.vehicleNo && errors.vehicleNo && <ErrorMsg>{errors.vehicleNo}</ErrorMsg>}
        <HelperText>{vehicleLookupLoading
            ? "Fetching insurance details…"
            : "Accepts any format — car, truck, crane, tractor, trailer, etc."}</HelperText>
      </Field>
          {vehicleError === "not_found" && (
        <div style={{ display:"flex", gap:10, alignItems:"flex-start", background:"#F7C1C1", border:"0.5px solid #E24B4A", borderRadius:8, padding:"10px 12px" }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink:0, marginTop:1 }}>
            <circle cx="8" cy="8" r="7" fill="#E24B4A" fillOpacity="0.15"/>
            <path d="M8 5v3" stroke="#A32D2D" strokeWidth="1.4" strokeLinecap="round"/>
            <circle cx="8" cy="11" r="0.7" fill="#A32D2D"/>
          </svg>
          <div>
            <p style={{ margin:0, fontSize:13, fontWeight:500, color:"#501313" }}>Vehicle not found</p>
            <p style={{ margin:"4px 0 0", fontSize:12, color:"#791F1F", lineHeight:1.5 }}>
              No records found for <strong style={{ fontFamily:"var(--wd-mono)" }}>{form.vehicleNo}</strong>
            </p>
          </div>
        </div>
      )}

      {vehicleError === "lookup_failed" && (
        <div style={{ display:"flex", gap:10, alignItems:"flex-start", background:"#FAEEDA", border:"0.5px solid #FAC775", borderRadius:8, padding:"10px 12px" }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink:0, marginTop:1 }}>
            <circle cx="8" cy="8" r="7" fill="#BA7517" fillOpacity="0.15"/>
            <path d="M8 5v3" stroke="#854F0B" strokeWidth="1.4" strokeLinecap="round"/>
            <circle cx="8" cy="11" r="0.7" fill="#854F0B"/>
          </svg>
          <div style={{ flex:1 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
              <p style={{ margin:0, fontSize:13, fontWeight:500, color:"#633806" }}>Lookup unavailable</p>
              <button onClick={onVehicleRetry} style={{ fontSize:12, color:"#854F0B", background:"transparent", border:"0.5px solid #EF9F27", borderRadius:6, padding:"3px 10px", cursor:"pointer" }}>
                Retry
              </button>
            </div>
            <p style={{ margin:"4px 0 0", fontSize:12, color:"#854F0B", lineHeight:1.5 }}>
              Could not reach the vehicle registry. You can retry or enter the details manually.
            </p>
          </div>
        </div>
      )}
      <div style={{ height:1, background:"var(--wd-border)", margin:"4px 0" }} />
 
      <SectionLabel icon="🛡️" title="Insurance Details" />
 
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
        <Field label="Policy Number" error={touched.policyNo && errors.policyNo}>
          <input
            className={`wd-input wd-mono ${touched.policyNo && errors.policyNo ? "input-error" : ""}`}
            placeholder="e.g. POL-2024-88821"
            value={form.policyNo}
            onChange={e => setField("policyNo", e.target.value.toUpperCase())}
          />
          {touched.policyNo && errors.policyNo && <ErrorMsg>{errors.policyNo}</ErrorMsg>}
        </Field>
 
        <Field label="Insurance Company">
          <input
            className="wd-input"
            placeholder="e.g. HDFC Ergo"
            value={form.insuranceCompany}
            onChange={e => setField("insuranceCompany", e.target.value)}
          />
        </Field>
      </div>

      <div style={{ height:1, background:"var(--wd-border)", margin:"4px 0" }} />

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
        <Field label="Make">
          <input
            className="wd-input"
            placeholder="Vehicle make"
            value={form.vehicleMake}
            readOnly
            style={{ background:"var(--wd-hover)", cursor:"default" }}
          />
          {form.vehicleMake && <HelperText style={{ marginTop:4 }}></HelperText>}
        </Field>

        <Field label="Model">
          <input
            className="wd-input"
            placeholder="Vehicle model"
            value={form.vehicleModel}
            readOnly
            style={{ background:"var(--wd-hover)", cursor:"default" }}
          />
        </Field>

        <Field label="Year of Manufacture">
          <input
            className="wd-input"
            placeholder="Year"
            value={form.vehicleYear}
            readOnly
            style={{ background:"var(--wd-hover)", cursor:"default" }}
          />
        </Field>

        <Field label="Body Type">
          <input
            className="wd-input"
            placeholder="Body type"
            value={form.vehicleBodyType}
            readOnly
            style={{ background:"var(--wd-hover)", cursor:"default" }}
          />
        </Field>
      </div>
    </>
  );
}

// ─── Step 3: Accident Details ─────────────────────────────────────────────────
function Step3({ form, errors, touched, setField }) {
  return (
    <>
      <SectionLabel icon="📍" title="Accident Details" subtitle="Date, time, and location of the accident." />

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
        <Field label="Accident Date" error={touched.accidentDate && errors.accidentDate}>
          <input
            className={`wd-input ${touched.accidentDate && errors.accidentDate ? "input-error" : ""}`}
            type="date"
            max={new Date().toISOString().split("T")[0]}
            value={form.accidentDate}
            onChange={e => setField("accidentDate", e.target.value)}
          />
          {touched.accidentDate && errors.accidentDate && <ErrorMsg>{errors.accidentDate}</ErrorMsg>}
        </Field>

        <Field label="Accident Time" error={touched.accidentTime && errors.accidentTime}>
          <input
            className={`wd-input ${touched.accidentTime && errors.accidentTime ? "input-error" : ""}`}
            type="time"
            value={form.accidentTime}
            onChange={e => setField("accidentTime", e.target.value)}
          />
          {touched.accidentTime && errors.accidentTime && <ErrorMsg>{errors.accidentTime}</ErrorMsg>}
        </Field>
      </div>

      <Field label="Accident Location / Landmark" error={touched.accidentLocation && errors.accidentLocation}>
        <div style={{ position:"relative" }}>
          <input
            className={`wd-input ${touched.accidentLocation && errors.accidentLocation ? "input-error" : ""}`}
            style={{ paddingLeft:36 }}
            placeholder="e.g. Connaught Place, Near PVR Cinemas"
            value={form.accidentLocation}
            onChange={e => setField("accidentLocation", e.target.value)}
          />
          <LocationInputIcon />
        </div>
        {touched.accidentLocation && errors.accidentLocation && <ErrorMsg>{errors.accidentLocation}</ErrorMsg>}
      </Field>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
        <Field label="Pincode" error={touched.accidentPincode && errors.accidentPincode}>
          <input
            className={`wd-input wd-mono ${touched.accidentPincode && errors.accidentPincode ? "input-error" : ""}`}
            placeholder="6-digit pincode"
            value={form.accidentPincode}
            onChange={e => setField("accidentPincode", e.target.value.replace(/\D/g,"").slice(0,6))}
            maxLength={6}
          />
          {touched.accidentPincode && errors.accidentPincode && <ErrorMsg>{errors.accidentPincode}</ErrorMsg>}
        </Field>

        <Field label="State">
          <input
            className="wd-input"
            placeholder="e.g. Delhi"
            value={form.accidentState}
            onChange={e => setField("accidentState", e.target.value)}
          />
        </Field>
      </div>

      <Field label="Brief Description (optional)">
        <textarea
          className="wd-input"
          rows={3}
          style={{ resize:"vertical" }}
          placeholder="Briefly describe how the accident occurred..."
          value={form.description}
          onChange={e => setField("description", e.target.value)}
        />
        <div style={{ display:"flex", justifyContent:"flex-end" }}>
          <span style={{ fontSize:10, color:"var(--wd-t3)" }}>{form.description.length}/500</span>
        </div>
      </Field>

      <div style={{ height:1, background:"var(--wd-border)", margin:"4px 0" }} />

      <PriorityPicker value={form.priority} onChange={v => setField("priority", v)} />
    </>
  );
}

// ─── Step 4: Review ───────────────────────────────────────────────────────────
function Step4({ form, ccEmails, setCcEmails, clientCcEmails  }) {
  const sections = [
    {
      title:"👤 Customer", 
      rows:[
        ["Email", form.customerEmail,  true  ],
        ["Phone", form.customerPhone || "Not provided", true  ],
        ["Name", form.customerName || "Not provided", false ],
        ["Client Name", form.clientName    || "Not provided", false],
      ]
    },
    {
      title:"🚗 Vehicle & Policy", 
      rows:[
        ["Vehicle No", form.vehicleNo.toUpperCase(), true  ],
        ["Policy No", form.policyNo.toUpperCase() || "Not provided", true  ],
        ["Insurance Co", form.insuranceCompany || "Not provided", false ],
        ["Make", form.vehicleMake || "Not provided", false ],
        ["Model", form.vehicleModel || "Not provided", false ],
        ["Year", form.vehicleYear || "Not provided", false ],
        ["Body Type", form.vehicleBodyType || "Not provided", false ],
      ]
    },
    {
      title:"📍 Accident", 
      rows:[
        ["Date",        form.accidentDate,                         false ],
        ["Time",        form.accidentTime,                         false ],
        ["Location",    form.accidentLocation,                     false ],
        ["Pincode",     form.accidentPincode,                      true  ],
        ["State",       form.accidentState    || "Not provided",   false ],
        ["Description", form.description      || "Not provided",   false ],
      ]
    },
  ];

  const priorityInfo = PRIORITIES.find(p => p.value === form.priority) || PRIORITIES[0];

  return (
    <>
      <SectionLabel icon="✅" title="Review & Submit" subtitle="Confirm all details before creating the case." />

      {sections.map(sec => (
        <div key={sec.title} className="wd-card" style={{ padding:16 }}>
          <div style={{ fontSize:12, fontWeight:700, color:"var(--wd-t2)", marginBottom:12 }}>{sec.title}</div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {sec.rows.map(([label, value, mono]) => (
              <div key={label} style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12 }}>
                <span style={{ fontSize:12, color:"var(--wd-t3)", flexShrink:0, minWidth:90 }}>{label}</span>
                <span style={{
                  fontSize:12,
                  color: value.includes("Not provided") ? "var(--wd-t3)" : "var(--wd-t1)",
                  fontFamily: mono ? "var(--wd-mono)" : "var(--wd-sans)",
                  fontWeight: mono ? 600 : 400,
                  textAlign:"right",
                  fontStyle: value.includes("Not provided") ? "italic" : "normal",
                }}>{value || "—"}</span>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Priority badge */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 16px", background: priorityInfo.bg, borderRadius:10, border:`1px solid ${priorityInfo.border}` }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:16 }}>{priorityInfo.icon}</span>
          <div>
            <div style={{ fontSize:12, fontWeight:700, color: priorityInfo.color }}>Priority: {priorityInfo.label}</div>
            <div style={{ fontSize:11, color:"var(--wd-t3)" }}>Expected resolution timeline</div>
          </div>
        </div>
        <div style={{ fontSize:13, fontWeight:800, color: priorityInfo.color }}>{priorityInfo.days} days</div>
      </div>

         {/* ── CC Recipients picker ─────────────────────────────────────────── */}
      <CcPicker ccEmails={ccEmails} setCcEmails={setCcEmails} customerEmail={form.customerEmail} clientCcEmails={clientCcEmails}/>

      {/* Email send preview */}
      <div style={{ display:"flex", gap:12, padding:"14px 16px", background:"rgba(34,197,94,.08)", borderRadius:12, border:"1px solid rgba(34,197,94,.2)" }}>
        <span style={{ fontSize:20, flexShrink:0 }}>📧</span>
        <div>
          <div style={{ fontWeight:700, fontSize:13, color:"var(--wd-green)", marginBottom:3 }}>Case registration email will be sent to:</div>
          <div style={{ fontFamily:"var(--wd-mono)", fontSize:13, color:"var(--wd-t1)" }}>{form.customerEmail}</div>
          <div style={{ fontSize:12, color:"var(--wd-t3)", marginTop:4 }}>
            The email will include: case ID, portal login link, document upload instructions, and support contact.
          </div>
        </div>
      </div>
    </>
  );
}

function CcPicker({ ccEmails, setCcEmails, customerEmail, clientCcEmails = []  }) {
  const [customInput, setCustomInput] = useState("");
  const [customError, setCustomError] = useState("");
 
  const togglePreset = (email) => {
    setCcEmails(prev =>
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
  };
 
  const addCustom = () => {
    const val = customInput.trim().toLowerCase();
    if (!val) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      setCustomError("Enter a valid email address");
      return;
    }
    if (val === customerEmail.toLowerCase()) {
      setCustomError("Customer email is already the primary recipient");
      return;
    }
    if (ccEmails.includes(val)) {
      setCustomError("Already added");
      return;
    }
    setCcEmails(prev => [...prev, val]);
    setCustomInput("");
    setCustomError("");
  };
 
  const removeEmail = (email) => {
    setCcEmails(prev => prev.filter(e => e !== email));
  };
 
  const handleKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); addCustom(); }
    if (e.key === "," || e.key === " ") { e.preventDefault(); addCustom(); }
  };
 
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
      {/* Section header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:16 }}>📬</span>
          <div>
            <div style={{ fontSize:13, fontWeight:700, color:"var(--wd-t1)" }}>CC Recipients</div>
            <div style={{ fontSize:11, color:"var(--wd-t3)" }}>Send a copy to additional contacts</div>
          </div>
        </div>
        {ccEmails.length > 0 && (
          <span style={{
            fontSize:10, fontWeight:700, color:"var(--wd-blue)",
            background:"var(--wd-blue-soft)", padding:"2px 8px", borderRadius:20,
          }}>{ccEmails.length} added</span>
        )}
      </div>
 
      {/* Quick-add preset chips */}
      <div style={{ display:"flex", flexWrap:"wrap", gap:7 }}>
        {CC_PRESETS.map(preset => {
          const active = ccEmails.includes(preset.email);
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => togglePreset(preset.email)}
              style={{
                display:"flex", alignItems:"center", gap:5,
                padding:"5px 11px",
                background: active ? "rgba(59,130,246,.12)" : "var(--wd-hover)",
                border: `1.5px solid ${active ? "var(--wd-blue)" : "var(--wd-border-md)"}`,
                borderRadius:20, cursor:"pointer",
                fontSize:12, fontWeight:600,
                color: active ? "var(--wd-blue)" : "var(--wd-t2)",
                transition:"all .15s",
              }}
            >
              {active
                ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                : <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              }
              {preset.label}
            </button>
          );
        })}
      </div>
 
      {/* Custom email input */}
      <div style={{ display:"flex", gap:8 }}>
        <div style={{ flex:1, position:"relative" }}>
          <input
            className={`wd-input ${customError ? "input-error" : ""}`}
            style={{ paddingLeft:32, fontSize:12 }}
            type="email"
            placeholder="Add custom email address…"
            value={customInput}
            onChange={e => { setCustomInput(e.target.value); setCustomError(""); }}
            onKeyDown={handleKeyDown}
          />
          {/* mail icon */}
          <svg style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", opacity:.4 }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--wd-t1)" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 7L2 7"/></svg>
        </div>
        <button
          type="button"
          className="wd-btn wd-btn-ghost wd-btn-sm"
          onClick={addCustom}
          style={{ flexShrink:0, fontSize:12 }}
        >
          + Add
        </button>
      </div>
      {customError && <ErrorMsg>{customError}</ErrorMsg>}
 
      {/* Active CC list */}
      {ccEmails.length > 0 && (
        <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
          {ccEmails.map(email => {
              const isFromClient = clientCcEmails.includes(email);
              return (
                <div key={email} style={{
                  display:"flex", alignItems:"center", gap:6,
                  padding:"4px 10px",
                  background: isFromClient ? "rgba(59,130,246,.07)" : "var(--wd-hover)",
                  border: `1px solid ${isFromClient ? "rgba(59,130,246,.25)" : "var(--wd-border-md)"}`,
                  borderRadius:20, fontSize:11,
                  color:"var(--wd-t2)",
                }}>
                {isFromClient && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--wd-blue)" strokeWidth="2.5" style={{ flexShrink:0 }}>
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                )}
                <span style={{ fontFamily:"var(--wd-mono)" }}>{email}</span>
                {isFromClient && (
                  <span style={{ fontSize:9, fontWeight:700, color:"var(--wd-blue)", opacity:.7, letterSpacing:".03em" }}>CLIENT</span>
                )}
              <button
                type="button"
                onClick={() => removeEmail(email)}
                style={{ background:"none", border:"none", cursor:"pointer", padding:0, display:"flex", color:"var(--wd-t3)", lineHeight:1 }}
              >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        );
      })}
        </div>
      )}
    </div>
  );
}

// ─── Success Screen ───────────────────────────────────────────────────────────
function SuccessScreen({ success, onClose, onCreateAnother }) {
  const [copied, setCopied] = useState(false);

  const copyId = () => {
    navigator.clipboard.writeText(success.caseId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{ padding:"40px 32px", display:"flex", flexDirection:"column", alignItems:"center", gap:20, textAlign:"center" }}>
      {/* Animated checkmark */}
      <div style={{
        width:72, height:72, borderRadius:"50%",
        background:"rgba(34,197,94,.12)",
        border:"2px solid rgba(34,197,94,.3)",
        display:"flex", alignItems:"center", justifyContent:"center",
        animation:"wdFadeIn .3s ease",
      }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      </div>

      <div>
        <div style={{ fontSize:20, fontWeight:800, color:"var(--wd-t1)", marginBottom:6 }}>Case Created Successfully!</div>
        <div style={{ fontSize:13, color:"var(--wd-t3)", lineHeight:1.6 }}>
          A registration email has been sent to<br />
          <span style={{ color:"var(--wd-t1)", fontWeight:600 }}>{success.email}</span>
          {success.ccEmails?.length > 0 && (
            <><br /><span style={{ fontSize:12 }}>CC: {success.ccEmails.join(", ")}</span></>
          )}
        </div>
      </div>

      {/* Case ID box */}
      <div style={{
        width:"100%", padding:"18px 20px",
        background:"var(--wd-hover)",
        border:"1px solid var(--wd-border-md)",
        borderRadius:12,
      }}>
        <div style={{ fontSize:11, color:"var(--wd-t3)", fontWeight:600, textTransform:"uppercase", letterSpacing:".06em", marginBottom:8 }}>
          Case ID
        </div>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:12 }}>
          <span style={{
            fontFamily:"var(--wd-mono)", fontSize:28, fontWeight:800,
            color:"var(--wd-blue)", letterSpacing:".05em",
          }}>
            {success.caseId}
          </span>
          <button
            className="wd-btn wd-btn-ghost wd-btn-sm"
            onClick={copyId}
            style={{ flexShrink:0 }}
          >
            {copied
              ? <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--wd-green)" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg> Copied</>
              : <><CopyIcon /> Copy</>
            }
          </button>
        </div>
      </div>

      {/* Email preview */}
      <div style={{
        width:"100%", background:"var(--wd-surface)",
        border:"1px solid var(--wd-border)",
        borderRadius:10, overflow:"hidden",
      }}>
        {/* Email header bar */}
        <div style={{ background:"var(--wd-hover)", padding:"10px 16px", display:"flex", alignItems:"center", gap:8, borderBottom:"1px solid var(--wd-border)" }}>
          <span style={{ fontSize:12, color:"var(--wd-t3)" }}>📧</span>
          <span style={{ fontSize:11, color:"var(--wd-t3)" }}>Email sent to <strong style={{ color:"var(--wd-t2)" }}>{success.email}</strong>
            {success.ccEmails?.length > 0 && <> · CC: <strong style={{ color:"var(--wd-t2)" }}>{success.ccEmails.length} recipient{success.ccEmails.length > 1 ? "s" : ""}</strong></>}
          </span>
        </div>
        {/* Email preview body */}
        <div style={{ padding:"16px" }}>
          <div style={{ fontSize:12, fontWeight:700, color:"var(--wd-t1)", marginBottom:8 }}>
            🚗 Your accident claim case has been registered
          </div>
          <div style={{ fontSize:12, color:"var(--wd-t3)", lineHeight:1.7 }}>
            <div>Case ID: <span style={{ fontFamily:"var(--wd-mono)", color:"var(--wd-blue)", fontWeight:700 }}>{success.caseId}</span></div>
            <div style={{ marginTop:4 }}>Please log in to our portal to complete your details, upload documents, and track your claim status.</div>
          </div>
        </div>
      </div>

      {/* What's next steps */}
      <div style={{ width:"100%", display:"flex", flexDirection:"column", gap:8 }}>
        <div style={{ fontSize:11, color:"var(--wd-t3)", fontWeight:600, textTransform:"uppercase", letterSpacing:".06em", marginBottom:2 }}>What happens next</div>
        {[
          { step:"1", text:"Customer receives email and logs in to fill driver details" },
          { step:"2", text:"Customer uploads RC, Policy, DL and accident photos"        },
          { step:"3", text:"Admin assigns spot surveyor for vehicle inspection"         },
          { step:"4", text:"Survey completed → Workshop assigned for repair"            },
        ].map(({ step, text }) => (
          <div key={step} style={{ display:"flex", gap:10, padding:"8px 12px", background:"var(--wd-hover)", borderRadius:8, textAlign:"left" }}>
            <div style={{ width:20, height:20, borderRadius:"50%", background:"var(--wd-blue-soft)", color:"var(--wd-blue)", fontSize:10, fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{step}</div>
            <span style={{ fontSize:12, color:"var(--wd-t2)", lineHeight:1.4 }}>{text}</span>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div style={{ display:"flex", gap:10, width:"100%" }}>
        <button className="wd-btn wd-btn-ghost" onClick={onCreateAnother} style={{ flex:1, justifyContent:"center" }}>
          + Create Another
        </button>
        <button className="wd-btn wd-btn-primary" onClick={onClose} style={{ flex:1, justifyContent:"center" }}>
          Done
        </button>
      </div>
    </div>
  );
}

// ─── Priority Picker ──────────────────────────────────────────────────────────
function PriorityPicker({ value, onChange }) {
  return (
    <div>
      <SectionLabel icon="⚡" title="Case Priority" subtitle="Select the severity level — this sets the resolution timeline." />
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginTop:8 }}>
        {PRIORITIES.map(p => {
          const selected = value === p.value;
          return (
            <button
              key={p.value}
              type="button"
              onClick={() => onChange(p.value)}
              style={{
                display:"flex", flexDirection:"column", alignItems:"flex-start",
                gap:4, padding:"12px 14px",
                background: selected ? p.bg : "var(--wd-hover)",
                border: `2px solid ${selected ? p.border : "var(--wd-border)"}`,
                borderRadius:10, cursor:"pointer", textAlign:"left",
                transition:"all .15s",
                outline: selected ? `2px solid ${p.color}22` : "none",
              }}
            >
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <span style={{ fontSize:15 }}>{p.icon}</span>
                <span style={{ fontSize:13, fontWeight:700, color: selected ? p.color : "var(--wd-t1)" }}>{p.label}</span>
                {selected && (
                  <svg style={{ marginLeft:"auto" }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={p.color} strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                )}
              </div>
              <span style={{ fontSize:11, color:"var(--wd-t3)" }}>{p.days}-day resolution timeline</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Small Helpers ────────────────────────────────────────────────────────────
function SectionLabel({ icon, title, subtitle }) {
  return (
    <div style={{ marginBottom:4 }}>
      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
        <span style={{ fontSize:18 }}>{icon}</span>
        <span style={{ fontSize:14, fontWeight:700, color:"var(--wd-t1)" }}>{title}</span>
      </div>
      {subtitle && <div style={{ fontSize:12, color:"var(--wd-t3)", marginTop:3, paddingLeft:26 }}>{subtitle}</div>}
    </div>
  );
}

function Field({ label, error, children, style }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6, ...style }}>
      <label style={{ fontSize:12, fontWeight:600, color: error ? "var(--wd-red)" : "var(--wd-t2)" }}>{label}</label>
      {children}
    </div>
  );
}

function ErrorMsg({ children }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:5, marginTop:2 }}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--wd-red)" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
      <span style={{ fontSize:11, color:"var(--wd-red)" }}>{children}</span>
    </div>
  );
}

function HelperText({ children }) {
  return <span style={{ fontSize:11, color:"var(--wd-t3)", lineHeight:1.4 }}>{children}</span>;
}

function SpinnerInline() {
  return (
    <div style={{ width:14, height:14, border:"2px solid rgba(255,255,255,.3)", borderTopColor:"#fff", borderRadius:"50%", animation:"wdSpin .7s linear infinite", flexShrink:0 }} />
  );
}